import React, { useEffect, useMemo, useRef, useState } from 'react'
import { WriteMessageForm } from 'components/inbox/WriteMessageForm'
import {
  createIssueInboxConversation,
  createReviewInboxConversation
} from 'components/inbox/services/createInboxConversation'
import { createInboxMessage } from 'components/inbox/services/createInboxMessage'
import { fetchInboxConversations } from 'components/inbox/services/fetchInboxConversations'
import type { InboxConversationDetail, InboxMessage, InboxParticipant, ReviewerDisplayState } from 'components/inbox/inboxTypes'
import type { Review as ReviewType } from 'components/supplier_show/prop_shapes/review'
import { MessageModalLayout } from 'components/inbox/shared/MessageModalLayout'
import { PrivateMessageRecipientsField } from 'components/inbox/message_modals/PrivateMessageRecipientsField'
import type { WriteMessageFormSendResult } from 'components/inbox/WriteMessageForm'
import type { InboxUserSearchResult } from 'components/inbox/services/fetchInboxUsers'
import { IssueLinkingNote } from 'components/inbox/message_modals/IssueLinkingNote'
import {
  appendMessageToConversation,
  buildReviewerRecipient,
  type IssueContext,
  type MessageSubjectReview,
  renderIssueConversationContent,
  renderMessageThread,
  useMessageSubjectReview
} from 'components/inbox/message_modals/messageModalSubjectHelpers'

interface Props {
  review: ReviewType
  reviewerDisplayState: ReviewerDisplayState
  onClose: () => void
  onConversationCreated?: (conversationId: number) => void
  issueContext?: IssueContext
}

type PrivateMessageSubject =
  | {
    kind: 'review'
    review: MessageSubjectReview
  }
  | {
    kind: 'issue'
    issue: IssueContext
  }

type CreatedConversationResult =
  | { kind: 'notifyParent', notify: () => void }
  | {
    kind: 'displayConversation'
    conversation: InboxConversationDetail
    conversationNotice: string | null
    notifyParent?: () => void
  }

interface PrivateMessageSubjectConfig {
  recipients: InboxParticipant[]
  recipientSubjectProps: { reviewId: number } | { issueId: number }
  contentAfterTextarea?: React.ReactNode
  showReviewTile: (conversationDetail: InboxConversationDetail | null) => boolean
  renderConversationContent: (
    conversationDetail: InboxConversationDetail | null,
    existingMessages: InboxMessage[],
    onActiveIssueClick?: () => void
  ) => React.ReactNode
  validateAdditionalParticipantIds: (additionalParticipantIds: number[]) => string | null
  createConversation: (
    messageBody: string,
    additionalParticipantIds: number[]
  ) => Promise<InboxConversationDetail>
  resolveCreatedConversation: (conversation: InboxConversationDetail) => CreatedConversationResult
}

const EXISTING_CONVERSATION_NOTICE =
  'A conversation about this review already exists with this group of participants. Click send to add your message to the existing conversation.'

const duplicateConversationNotice = (
  conversation: InboxConversationDetail
): string | null => {
  const nonCanaryMessages = conversation.messages.filter((message) => message.system !== 'canary')

  return nonCanaryMessages.length > 1
    ? EXISTING_CONVERSATION_NOTICE
    : null
}

const buildPrivateMessageSubject = (
  review: MessageSubjectReview,
  issueContext?: IssueContext
): PrivateMessageSubject => (
  issueContext == null
    ? { kind: 'review', review }
    : { kind: 'issue', issue: issueContext }
)

const buildPrivateMessageConfig = (
  subject: PrivateMessageSubject,
  onConversationCreated?: (conversationId: number) => void
): PrivateMessageSubjectConfig => {
  switch (subject.kind) {
    case 'review':
      return {
        recipients: buildReviewerRecipient(subject.review),
        recipientSubjectProps: { reviewId: subject.review.id },
        showReviewTile: () => true,
        renderConversationContent: (_conversationDetail, existingMessages) => renderMessageThread(existingMessages),
        validateAdditionalParticipantIds: () => null,
        createConversation: async (
          messageBody,
          additionalParticipantIds
        ) => await createReviewInboxConversation(subject.review.id, messageBody, additionalParticipantIds),
        resolveCreatedConversation: (conversation) => {
          if (onConversationCreated != null) {
            return {
              kind: 'notifyParent',
              notify: () => onConversationCreated(conversation.id)
            }
          }

          return {
            kind: 'displayConversation',
            conversation,
            conversationNotice: duplicateConversationNotice(conversation)
          }
        }
      }
    case 'issue':
      return {
        recipients: [],
        recipientSubjectProps: { issueId: subject.issue.issueId },
        contentAfterTextarea: <IssueLinkingNote issueName={subject.issue.issueName} />,
        showReviewTile: (conversationDetail) => conversationDetail == null,
        renderConversationContent: renderIssueConversationContent,
        validateAdditionalParticipantIds: (additionalParticipantIds) => (
          additionalParticipantIds.length === 0
            ? 'Add at least one recipient before sending a private message.'
            : null
        ),
        createConversation: async (
          messageBody,
          additionalParticipantIds
        ) => await createIssueInboxConversation(
          subject.issue.issueId,
          messageBody,
          additionalParticipantIds
        ),
        resolveCreatedConversation: (conversation) => {
          if (onConversationCreated == null) {
            return {
              kind: 'displayConversation',
              conversation,
              conversationNotice: null
            }
          }

          return {
            kind: 'displayConversation',
            conversation,
            conversationNotice: null,
            notifyParent: () => onConversationCreated(conversation.id)
          }
        }
      }
  }
}

export const PrivateMessageModal: React.FC<Props> = ({
  review,
  reviewerDisplayState,
  onClose,
  onConversationCreated,
  issueContext
}) => {
  const subjectReview = useMessageSubjectReview(review)
  const subjectIssue = useMemo<IssueContext | undefined>(() => (
    issueContext == null
      ? undefined
      : {
          issueId: issueContext.issueId,
          issueName: issueContext.issueName
        }
  ), [issueContext?.issueId, issueContext?.issueName])
  const subject = useMemo(
    () => buildPrivateMessageSubject(subjectReview, subjectIssue),
    [subjectIssue, subjectReview]
  )
  const subjectConfig = useMemo(
    () => buildPrivateMessageConfig(subject, onConversationCreated),
    [onConversationCreated, subject]
  )
  const hasIssueContext = subjectIssue != null
  const loadDuplicateConversationNotice = useMemo<((additionalParticipantIds: number[]) => Promise<string | null>) | null>(() => {
    if (hasIssueContext) return null

    return async (additionalParticipantIds) => {
      const conversations = await fetchInboxConversations(
        subjectReview.id,
        additionalParticipantIds,
        true
      )

      return conversations.length > 0 ? EXISTING_CONVERSATION_NOTICE : null
    }
  }, [hasIssueContext, subjectReview.id])

  const [sendError, setSendError] = useState<string | null>(null)
  const [existingConversationNotice, setExistingConversationNotice] = useState<string | null>(null)
  const [conversationId, setConversationId] = useState<number | null>(null)
  const [conversationDetail, setConversationDetail] = useState<InboxConversationDetail | null>(null)
  const [existingMessages, setExistingMessages] = useState<InboxMessage[]>([])
  const [selectedAdditionalRecipients, setSelectedAdditionalRecipients] = useState<InboxUserSearchResult[]>([])
  const scrollRegionRef = useRef<HTMLDivElement>(null)
  const contentAfterTextarea = conversationId == null ? subjectConfig.contentAfterTextarea : null

  const setExistingConversationState = (
    nextConversationId: number,
    messages: InboxMessage[],
    conversationNotice: string | null = null
  ): void => {
    setConversationId(nextConversationId)
    setExistingMessages(messages)
    setExistingConversationNotice(conversationNotice)
  }

  const appendMessage = (message: InboxMessage): void => {
    setExistingMessages((previousMessages) => [...previousMessages, message])
    setConversationDetail((previousConversation) => appendMessageToConversation(previousConversation, message))
  }

  useEffect(() => {
    if (scrollRegionRef.current == null || conversationId == null) return
    scrollRegionRef.current.scrollTop = scrollRegionRef.current.scrollHeight
  }, [conversationId, existingMessages.length])

  useEffect(() => {
    if (conversationId != null) return

    if (loadDuplicateConversationNotice == null) {
      setExistingConversationNotice(null)
      return
    }

    let isMounted = true

    const loadExistingConversation = async (): Promise<void> => {
      try {
        const conversationNotice = await loadDuplicateConversationNotice(
          selectedAdditionalRecipients.map((recipient) => recipient.id)
        )
        if (!isMounted) return

        setExistingConversationNotice(conversationNotice)
      } catch (error) {
        console.error('Failed to load existing conversations:', error)
      }
    }

    void loadExistingConversation()

    return () => {
      isMounted = false
    }
  }, [conversationId, loadDuplicateConversationNotice, selectedAdditionalRecipients])

  const handleCreateConversation = async (
    messageBody: string
  ): Promise<WriteMessageFormSendResult | undefined> => {
    setSendError(null)

    try {
      if (conversationId == null) {
        const additionalParticipantIds = selectedAdditionalRecipients.map((recipient) => recipient.id)
        const validationError = subjectConfig.validateAdditionalParticipantIds(additionalParticipantIds)

        if (validationError != null) {
          setSendError(validationError)
          return 'keep_draft'
        }

        const conversation = await subjectConfig.createConversation(messageBody, additionalParticipantIds)
        const createdConversationResult = subjectConfig.resolveCreatedConversation(conversation)

        if (createdConversationResult.kind === 'notifyParent') {
          createdConversationResult.notify()
        } else {
          setConversationDetail(createdConversationResult.conversation)
          setExistingConversationState(
            createdConversationResult.conversation.id,
            createdConversationResult.conversation.messages,
            createdConversationResult.conversationNotice
          )
          if (createdConversationResult.notifyParent != null) {
            createdConversationResult.notifyParent()
          }
        }
        return 'sent'
      } else {
        const message = await createInboxMessage(conversationId, messageBody)
        appendMessage(message)
        return 'sent'
      }
    } catch {
      setSendError('Unable to send private message. Please try again.')
    }
  }

  return (
    <MessageModalLayout
      review={review}
      title='Private Message'
      onClose={onClose}
      testId='private-message-modal'
      showReviewTile={subjectConfig.showReviewTile(conversationDetail)}
      issueTrackingCTAMode={hasIssueContext ? 'activeOnly' : undefined}
      onActiveIssueClick={hasIssueContext ? onClose : undefined}
      supplierLinkMode={hasIssueContext ? 'canary' : undefined}
      recipientField={(
        <PrivateMessageRecipientsField
          participants={subjectConfig.recipients}
          showAddRecipient={conversationId == null}
          {...subjectConfig.recipientSubjectProps}
          contractName={review.contract}
          onAdditionalRecipientsChange={setSelectedAdditionalRecipients}
        />
      )}
      scrollRegionRef={scrollRegionRef}
      scrollableContent={
        conversationId != null
          ? (
            <div className='message-reviewer-modal__existing-conversation inbox-message-container'>
              <div className='inbox-message-scroll'>
                {subjectConfig.renderConversationContent(conversationDetail, existingMessages, hasIssueContext ? onClose : undefined)}
              </div>
            </div>
            )
          : null
      }
      footerContent={
        <>
          {sendError != null && (
            <div className='write-message-error' role='alert'>
              {sendError}
            </div>
          )}

          <WriteMessageForm
            reviewerDisplayState={reviewerDisplayState}
            disableSend={false}
            contentAfterTextarea={
              <>
                {existingConversationNotice != null && (
                  <div className='write-message-existing-conversation-note' role='status'>
                    <div className='write-message-existing-conversation-note__title'>
                      Duplicate Conversation
                    </div>
                    <div className='write-message-existing-conversation-note__body'>
                      {existingConversationNotice}
                    </div>
                  </div>
                )}

                {contentAfterTextarea}
              </>
            }
            onSend={handleCreateConversation}
            onCancel={onClose}
            showShareWithSupplierCTA={false}
          />
        </>
      }
    />
  )
}
